import { useState, useEffect, useCallback } from 'react';
import { Page } from '../../../../../components/page/page';
import { HasAccess } from '../../../../../features/has-access/has-access';
import { Card } from '../../../../../components/card/card';
import { Loader } from '../../../../../components/loader/loader';
import { DashboardCharts } from '../dashboard-components/dashboard-charts/dashboard-charts';
import { DashboardEmpty } from '../dashboard-components/dashboard-empty/dashboard-empty';
import {
  DashboardExternalMonitoringFilters,
  ExternalMonitoringFiltersState,
} from './dashboard-external-monitoring-filters';
import {
  buildExternalMonitoringPeriod,
  withClampedExternalMonitoringGrouping,
} from '../../../../../helpers/external-monitoring-period';
import { isExternalMonitoringDashboardDataEmpty } from './external-monitoring-dashboard-model';
import { useDashboardExternalMonitoring } from './use-dashboard-external-monitoring';
import { DashboardExternalMonitoringKpis } from './dashboard-external-monitoring-kpis/dashboard-external-monitoring-kpis.tsx';
import s from './dashboard-external-monitoring.module.css';

const defaultPeriod = buildExternalMonitoringPeriod('1');

const defaultFilters: ExternalMonitoringFiltersState = withClampedExternalMonitoringGrouping({
  sourceId: 'all',
  periodBeginSec: defaultPeriod.begin,
  periodEndSec: defaultPeriod.end,
  groupingInterval: 'day',
  categoryId: 'all',
  // aspectId: 'all',
  sentimentKey: 'all',
});

export const DashboardExternalMonitoring = () => {
  const [filters, setFilters] = useState<ExternalMonitoringFiltersState>(defaultFilters);

  const applyFiltersPatch = useCallback((patch: Partial<ExternalMonitoringFiltersState>) => {
    setFilters((prev) => withClampedExternalMonitoringGrouping({ ...prev, ...patch }));
  }, []);

  const {
    chartData,
    kpiPayload,
    toneNetSparklineValues,
    sourceOptions,
    periodLabel,
    availableResources,
    externalResourceCategories,
    dashboardModel,
    isDashboardLoading,
  } = useDashboardExternalMonitoring({
    filters,
  });

  /** id в фильтре только из ответа aggregation/available (кроме «Все источники») */
  useEffect(() => {
    if (availableResources.length === 0) return;
    if (filters.sourceId === 'all') return;
    const n = Number(filters.sourceId);
    if (!Number.isFinite(n)) return;
    if (!availableResources.some((r) => r.id === n)) {
      applyFiltersPatch({ sourceId: String(availableResources[0].id) });
    }
  }, [availableResources, filters.sourceId, applyFiltersPatch]);

  /** Выбранная категория должна быть в справочнике GET /categories */
  useEffect(() => {
    if (externalResourceCategories.length === 0) return;
    if (filters.categoryId === 'all') return;
    if (!externalResourceCategories.some((c) => String(c.id) === filters.categoryId)) {
      applyFiltersPatch({ categoryId: 'all' });
    }
  }, [externalResourceCategories, filters.categoryId, applyFiltersPatch]);

  // Валидация aspectId при включённом фильтре по аспекту (см. закоммент. aspectId в типе и applyAspectFilter в модели).
  // useEffect(() => {
  //   if (filters.aspectId === 'all') return;
  //   if (dashboardModel == null) return;
  //   const id = Number(filters.aspectId);
  //   if (!Number.isFinite(id)) {
  //     applyFiltersPatch({ aspectId: 'all' });
  //     return;
  //   }
  //   if (dashboardModel.aspectNamesById[id] == null) {
  //     applyFiltersPatch({ aspectId: 'all' });
  //   }
  // }, [dashboardModel, filters.aspectId, applyFiltersPatch]);

  const showChartsLoading = isDashboardLoading;
  const showFullEmpty =
    !isDashboardLoading &&
    dashboardModel != null &&
    isExternalMonitoringDashboardDataEmpty(dashboardModel);

  return (
    <HasAccess service="dashboard" permissions={['read', 'write']}>
      <Page>
        <DashboardExternalMonitoringFilters
          filters={filters}
          onFiltersChange={applyFiltersPatch}
          sourceOptions={sourceOptions}
          categories={externalResourceCategories}
          periodLabel={periodLabel}
        />
        {showChartsLoading ? (
          <Card className={s.emptyStateCard}>
            <div className={s.emptyStateBody}>
              <Loader size="lg" />
            </div>
          </Card>
        ) : showFullEmpty ? (
          <Card className={s.emptyStateCard}>
            <div className={s.emptyStateBody}>
              <DashboardEmpty />
            </div>
          </Card>
        ) : (
          <>
            <DashboardExternalMonitoringKpis
              filters={filters}
              kpiPayload={kpiPayload}
              toneNetSparklineValues={toneNetSparklineValues}
              onFiltersChange={applyFiltersPatch}
            />
            <DashboardCharts
              className={s.chartsGrid}
              chartsData={chartData}
              externalChartsGrid
            />
          </>
        )}
      </Page>
    </HasAccess>
  );
};
